import { FC, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useApi } from 'api/ApiProvider';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import type { InvoiceType } from 'axiosApi/models';
import type { CreateInvoiceTypeCommand } from 'axiosApi/models/create-invoice-type-command';
import type { UpdateInvoiceTypeCommand } from 'axiosApi/models/update-invoice-type-command';

type InvoiceTypeFormData = {
  name: string;
  description?: string;
  idCountry: number;
};

type InvoiceTypeFormDrawerProps = {
  open: boolean;
  invoiceType: InvoiceType | null;
  onClose: () => void;
  onSuccess: () => void;
  countries: any[];
};

const InvoiceTypeFormDrawer: FC<InvoiceTypeFormDrawerProps> = ({
  open,
  invoiceType,
  onClose,
  onSuccess,
  countries,
}) => {
  const api = useApi();
  const toast = useToastMessageQueue();
  const isEditMode = !!invoiceType;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InvoiceTypeFormData>({
    defaultValues: {
      name: '',
      description: '',
      idCountry: 0,
    },
  });

  useEffect(() => {
    if (!open) {
      reset({ name: '', description: '', idCountry: 0 });
      return;
    }
    if (invoiceType) {
      reset({
        name: invoiceType.name || '',
        description: invoiceType.description || '',
        idCountry: invoiceType.idCountry || 0,
      });
    } else {
      reset({ name: '', description: '', idCountry: 0 });
    }
  }, [open, invoiceType, reset]);

  const createMutation = useMutation({
    mutationFn: async (data: CreateInvoiceTypeCommand) => {
      return await api.invoiceApi.apiVversionInvoiceTypePost('1', data);
    },
    onSuccess: () => {
      toast.success({ body: intl.get('invoiceTypeManagement.createSuccess') });
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error({
        header: intl.get('invoiceTypeManagement.createError'),
        body: error?.message ?? intl.get('invoiceTypeManagement.createError'),
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateInvoiceTypeCommand) => {
      return await api.invoiceApi.apiVversionInvoiceTypePut('1', data);
    },
    onSuccess: () => {
      toast.success({ body: intl.get('invoiceTypeManagement.updateSuccess') });
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error({
        header: intl.get('invoiceTypeManagement.updateError'),
        body: error?.message ?? intl.get('invoiceTypeManagement.updateError'),
      });
    },
  });

  const onSubmit = (data: InvoiceTypeFormData) => {
    const payload = {
      name: data.name,
      description: data.description?.trim() ?? '',
      idCountry: data.idCountry,
    };
    if (isEditMode && invoiceType?.id != null) {
      updateMutation.mutate({ id: invoiceType.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 500, p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {isEditMode
            ? intl.get('invoiceTypeManagement.edit')
            : intl.get('invoiceTypeManagement.create')}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <Controller
              name="name"
              control={control}
              rules={{ required: intl.get('invoiceTypeManagement.nameRequired') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={intl.get('invoiceTypeManagement.name')}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  required
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={intl.get('invoiceTypeManagement.description')}
                  fullWidth
                  multiline
                  rows={3}
                />
              )}
            />

            <Controller
              name="idCountry"
              control={control}
              rules={{
                required: intl.get('invoiceTypeManagement.countryRequired'),
                validate: (value) => value > 0 || intl.get('invoiceTypeManagement.countryRequired'),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label={intl.get('invoiceTypeManagement.country')}
                  error={!!errors.idCountry}
                  helperText={errors.idCountry?.message}
                  fullWidth
                  required
                >
                  <MenuItem value={0} disabled>
                    {intl.get('invoiceTypeManagement.selectCountry')}
                  </MenuItem>
                  {countries.map((country: any) => (
                    <MenuItem key={country.id} value={country.id}>
                      {intl.get(country.labelKey ?? country.label ?? '')}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isPending}
              >
                {isEditMode
                  ? intl.get('invoiceTypeManagement.update')
                  : intl.get('invoiceTypeManagement.create')}
              </Button>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }}
                disabled={isPending}
              >
                {intl.get('invoiceTypeManagement.cancel')}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Box>
    </Drawer>
  );
};

export default InvoiceTypeFormDrawer;
